// Ported literally from OCs-builder/src/utils/PDFGenerator.jsx (the source of truth
// for the PDF layout) with four deliberate changes, all marked with `CHANGE:`:
//   1. Fonts and logos are EMBEDDED as base64 — no runtime fetch (fetchAsBase64 /
//      fetchImageAsDataURL removed). A failed fetch silently falls back to Helvetica,
//      changing glyph widths and pushing text out of the columns.
//   2. Dates are formatted from the "YYYY-MM-DD" string directly (no new Date(),
//      which would shift the day across timezones).
//   3. RBA is read from the backend-computed client.isRBA flag (not client.id).
//   4. The oc-proveedores-incluye rows.map now copies useDateRange so the
//      Inicio/Fin date range is actually rendered.
// Plus: the output filename is {issuerCode}-{slug(clientLabel)}-{ocDate}.pdf.
//
// jsPDF is pinned to EXACTLY 3.0.2 in package.json — splitTextToSize / text render
// change between versions and would alter wrapping.

/* eslint-disable @typescript-eslint/no-explicit-any */
import { jsPDF } from "jspdf";
import { MONTSERRAT_REGULAR_B64, MONTSERRAT_BOLD_B64 } from "./ocFonts";
import { getOcLogo } from "./ocLogos";
import {
  GenerateOcPdfArgs,
  OcItem,
  OcItemMedios,
  OcItemCreatividades,
  OcItemProveedores,
  OcItemProveedoresIncluye,
  OcTotalsMedios,
  OcTotalsCreatividades,
} from "../../types/oc";

// ========= Helpers tipografía (Montserrat) =========
// CHANGE 1: register embedded base64 fonts, no fetch.
function loadPdfFonts(doc: jsPDF) {
  doc.addFileToVFS("Montserrat-Regular.ttf", MONTSERRAT_REGULAR_B64);
  doc.addFont("Montserrat-Regular.ttf", "Montserrat", "normal");

  doc.addFileToVFS("Montserrat-Bold.ttf", MONTSERRAT_BOLD_B64);
  doc.addFont("Montserrat-Bold.ttf", "Montserrat", "bold");

  doc.setFont("Montserrat", "normal");
}

// ========= Helpers numéricos y formato =========
function only2dec(n: unknown): number {
  const v = Number(n);
  if (Number.isNaN(v)) return 0;
  return Math.round(v * 100) / 100;
}

function formatEUR(value: unknown): string {
  const num = Number(value || 0);
  return num.toLocaleString("es-ES", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 2,
  });
}

// CHANGE 2: format "YYYY-MM-DD" directly to "DD/MM/YYYY", no timezone shift.
function formatDateES(isoDate?: string): string {
  if (!isoDate) return "";
  const m = String(isoDate).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return String(isoDate);
  const [, y, mo, d] = m;
  return `${d}/${mo}/${y}`;
}

function slug(s: string): string {
  return String(s || "")
    .toLowerCase()
    .replace(/[^a-z0-9\-_]+/gi, "_");
}

/**
 * Genera y descarga un PDF de Orden de Compra para los 4 modos.
 */
export async function generateOcPdf({
  mode,
  company,
  client,
  items,
  totals,
  ocDate,
  ocNumber,
  campaign,
  showCompanyContact = true,
  showClientContact = true,
  compactMode = false,
}: GenerateOcPdfArgs): Promise<void> {
  const pageW = 188;
  const pageH = 260;
  const doc = new jsPDF({
    orientation: "portrait",
    unit: "mm",
    format: [pageW, pageH],
  });

  loadPdfFonts(doc);

  const margin = 10;
  const contentW = pageW - margin * 2;

  // --- Logo ---
  // CHANGE 1 + logo-ratio fix: embed the logo by issuer code and compute the
  // height synchronously from the known PNG dimensions (the original used an async
  // img.onload so originalH was still 0 at addImage time → logo invisible/wrong).
  const y = margin;
  const logoMaxW = 40;
  const logo = getOcLogo(company.code);
  if (logo) {
    try {
      const logoH = logoMaxW * (logo.height / logo.width);
      doc.addImage(
        logo.dataUrl,
        "PNG",
        margin,
        y,
        logoMaxW,
        logoH,
        undefined,
        "FAST"
      );
    } catch (e) {
      console.error("Error al añadir logo", e);
    }
  }

  // --- Título ---
  doc.setFont("Montserrat", "bold");
  doc.setFontSize(16);
  doc.setTextColor(0);
  doc.text("ORDEN DE COMPRA", pageW / 2, y + 10, { align: "center" });
  doc.setFont("Montserrat", "normal");

  // --- Meta info ---
  const metaY = y + 18; // aprox. tras logo
  doc.setFontSize(11);
  // CHANGE 2: formato de fecha directo desde el string.
  const formattedDate = formatDateES(ocDate);
  doc.text(`Fecha: ${formattedDate}`, margin, metaY);
  doc.text(`Nº: ${ocNumber}`, pageW - margin, metaY, { align: "right" });
  doc.setFont("Montserrat", "bold");
  doc.text(`Campaña: ${campaign || ""}`, pageW / 2, metaY + 7, {
    align: "center",
  });
  doc.setFont("Montserrat", "normal");

  // --- Tabla conceptos ---
  const tableTop = metaY + 12;
  const headerH = compactMode ? 7 : 10;
  const rowH = compactMode ? 5.5 : 8;
  const colW: any =
    mode === "oc-medios"
      ? { concepto: 77, inv: 25, fee: 25, total: 41 }
      : mode === "oc-creatividades"
        ? { concepto: 77, cantidad: 25, precio: 25, total: 41 }
        : mode === "oc-proveedores"
          ? { concepto: 77, inv: 25, kpi: 11, costKpi: 20, result: 35 }
          : mode === "oc-proveedores-incluye"
            ? { concepto: 30, fechas: 35, incluye: 72, coste: 31 }
            : null;

  // Cabecera. Va en una funcion porque se repinta al principio de cada
  // pagina cuando la tabla de conceptos desborda.
  const drawTableHeader = (topY: number) => {
    doc.setFont("Montserrat", "bold");
    doc.setFontSize(compactMode ? 8 : 10);
    doc.rect(margin, topY, contentW, headerH);
    const headerTextY = compactMode ? topY + 3.8 : topY + 5.5;
    const headerTextYMulti = compactMode ? topY + 2.2 : topY + 3.4;
    let x = margin;
    if (mode === "oc-medios") {
      doc.text("CONCEPTO", x + colW.concepto / 2, headerTextY, {
        baseline: "middle",
        align: "center",
      });
      x += colW.concepto;
      doc.text("INVERSIÓN\nEN MEDIOS", x + colW.inv / 2, headerTextYMulti, {
        baseline: "middle",
        align: "center",
      });
      x += colW.inv;
      doc.text("FEE", x + colW.fee / 2, headerTextY, {
        baseline: "middle",
        align: "center",
      });
      x += colW.fee;
      doc.text("TOTAL", x + colW.total / 2, headerTextY, {
        baseline: "middle",
        align: "center",
      });
    } else if (mode === "oc-creatividades") {
      doc.text("CONCEPTO", x + colW.concepto / 2, headerTextY, {
        baseline: "middle",
        align: "center",
      });
      x += colW.concepto;
      doc.text("CANTIDAD", x + colW.cantidad / 2, headerTextY, {
        baseline: "middle",
        align: "center",
      });
      x += colW.cantidad;
      doc.text("PRECIO\nUNITARIO", x + colW.precio / 2, headerTextYMulti, {
        baseline: "middle",
        align: "center",
      });
      x += colW.precio;
      doc.text("TOTAL", x + colW.total / 2, headerTextY, {
        baseline: "middle",
        align: "center",
      });
    } else if (mode === "oc-proveedores") {
      doc.text("CONCEPTO", x + colW.concepto / 2, headerTextY, {
        baseline: "middle",
        align: "center",
      });
      x += colW.concepto;
      doc.text("INVERSION", x + colW.inv / 2, headerTextY, {
        baseline: "middle",
        align: "center",
      });
      x += colW.inv;
      doc.text("KPI", x + colW.kpi / 2, headerTextY, {
        baseline: "middle",
        align: "center",
      });
      x += colW.kpi;
      doc.text("COSTE KPI", x + colW.costKpi / 2, headerTextY, {
        baseline: "middle",
        align: "center",
      });
      x += colW.costKpi;
      doc.text("RESULTADO", x + colW.result / 2, headerTextY, {
        baseline: "middle",
        align: "center",
      });
      x += colW.result;
    } else if (mode === "oc-proveedores-incluye") {
      doc.text("CAMPAÑA", x + colW.concepto / 2, headerTextY, {
        baseline: "middle",
        align: "center",
      });
      x += colW.concepto;
      doc.text("FECHAS", x + colW.fechas / 2, headerTextY, {
        baseline: "middle",
        align: "center",
      });
      x += colW.fechas;
      doc.text("¿QUE INCLUYE?", x + colW.incluye / 2, headerTextY, {
        baseline: "middle",
        align: "center",
      });
      x += colW.incluye;
      doc.text("COSTE", x + colW.coste / 2, headerTextY, {
        baseline: "middle",
        align: "center",
      });
      x += colW.coste;
    }
    doc.setFont("Montserrat", "normal");
  };

  drawTableHeader(tableTop);
  let currentY = tableTop + headerH;
  const reservedBottom = compactMode ? 60 : 120; //chapuza gorda
  // Limite inferior de la tabla en las paginas que no llevan el pie (totales,
  // datos fiscales, metodo de pago y firmas): solo el margen inferior.
  const tableBottomLimit = pageH - margin;
  const maxLettersPerRowConcepto =
    mode === "oc-medios"
      ? 53
      : mode === "oc-creatividades"
        ? 53
        : mode === "oc-proveedores"
          ? 53
          : mode === "oc-proveedores-incluye"
            ? 21
            : 53;

  const wrappedWidthConcepto = maxLettersPerRowConcepto * 1.36; // ~1.36mm por letra
  const maxLettersPerRowIncluye = 50;
  const wrappedWidthIncluye = maxLettersPerRowIncluye * 1.36;

  // Normaliza filas según modo
  const rows = items.map((it: OcItem) => {
    if (mode === "oc-medios") {
      const m = it as OcItemMedios;
      return {
        concepto: m.concepto,
        inversion: only2dec(m.inversion),
        fee: only2dec(m.fee),
        total: only2dec(only2dec(m.inversion) + only2dec(m.fee)),
      } as any;
    } else if (mode === "oc-creatividades") {
      const c = it as OcItemCreatividades;
      return {
        concepto: c.concepto,
        cantidad: only2dec(c.cantidad),
        precio: only2dec(c.precio),
        total: only2dec(Number(c.cantidad) * Number(c.precio)),
      } as any;
    } else if (mode === "oc-proveedores") {
      const p = it as OcItemProveedores;
      return {
        concepto: p.concepto,
        inv: only2dec(p.inv),
        kpi: p.KPI || "",
        costKpi: only2dec(p.costKPI),
        result: p.result,
      } as any;
    } else if (mode === "oc-proveedores-incluye") {
      const pi = it as OcItemProveedoresIncluye;
      return {
        concepto: pi.concepto || "",
        // CHANGE 4: copiar useDateRange para que el rango Inicio/Fin se pinte.
        useDateRange: pi.useDateRange || false,
        fechaText: pi.fechaText || "",
        fechaInicio: pi.fechaInicio || "",
        fechaFin: pi.fechaFin || "",
        incluye: pi.incluye || "",
        coste: only2dec(pi.coste),
      } as any;
    }
    return null;
  });

  const lineHeight = compactMode ? 2.5 : 3.5;
  const calculateTextHeight = (text: string, maxLettersPerRow: number) => {
    if (!text) return rowH;
    const lines = text.split("\n");
    let totalHeight = rowH - lineHeight;
    lines.forEach((line) => {
      totalHeight +=
        lineHeight * Math.ceil(String(line).length / (maxLettersPerRow / 1.4));
    });
    return totalHeight;
  };

  // Se pintan TODAS las filas: la tabla ya no se recorta. Cuando una fila no cabe
  // en la pagina actual se abre otra y se repinta la cabecera.
  let rowsOnPage = 0;
  rows.forEach((r: any) => {
    let cx = margin;
    // Ajusta altura de fila según cantidad de lineas del concepto.
    let rowHwithText = r.concepto
      ? calculateTextHeight(r.concepto, maxLettersPerRowConcepto)
      : rowH;

    if (mode === "oc-proveedores-incluye") {
      const rowHConcepto = r.concepto
        ? calculateTextHeight(r.concepto, maxLettersPerRowConcepto)
        : rowH;
      const rowHIncluye = r.incluye
        ? calculateTextHeight(r.incluye, maxLettersPerRowIncluye)
        : rowH;
      rowHwithText = Math.max(rowHwithText, rowHConcepto, rowHIncluye);
    }

    // Salto de pagina si la fila no cabe. Nunca se descarta una fila.
    if (rowsOnPage > 0 && currentY + rowHwithText > tableBottomLimit) {
      doc.addPage([pageW, pageH], "portrait");
      currentY = margin;
      drawTableHeader(currentY);
      currentY += headerH;
      rowsOnPage = 0;
    }
    rowsOnPage++;

    // Dibuja rectángulo
    doc.rect(margin, currentY, contentW, rowHwithText);

    if (mode === "oc-medios") {
      doc.line(margin + colW.concepto, currentY, margin + colW.concepto, currentY + rowHwithText);
      doc.line(margin + colW.concepto + colW.inv, currentY, margin + colW.concepto + colW.inv, currentY + rowHwithText);
      doc.line(margin + colW.concepto + colW.inv + colW.fee, currentY, margin + colW.concepto + colW.inv + colW.fee, currentY + rowHwithText);
    } else if (mode === "oc-creatividades") {
      doc.line(margin + colW.concepto, currentY, margin + colW.concepto, currentY + rowHwithText);
      doc.line(margin + colW.concepto + colW.cantidad, currentY, margin + colW.concepto + colW.cantidad, currentY + rowHwithText);
      doc.line(margin + colW.concepto + colW.cantidad + colW.precio, currentY, margin + colW.concepto + colW.cantidad + colW.precio, currentY + rowHwithText);
    } else if (mode === "oc-proveedores") {
      doc.line(margin + colW.concepto, currentY, margin + colW.concepto, currentY + rowHwithText);
      doc.line(margin + colW.concepto + colW.inv, currentY, margin + colW.concepto + colW.inv, currentY + rowHwithText);
      doc.line(margin + colW.concepto + colW.inv + colW.kpi, currentY, margin + colW.concepto + colW.inv + colW.kpi, currentY + rowHwithText);
      doc.line(margin + colW.concepto + colW.inv + colW.kpi + colW.costKpi, currentY, margin + colW.concepto + colW.inv + colW.kpi + colW.costKpi, currentY + rowHwithText);
    } else if (mode === "oc-proveedores-incluye") {
      doc.line(margin + colW.concepto, currentY, margin + colW.concepto, currentY + rowHwithText);
      doc.line(margin + colW.concepto + colW.fechas, currentY, margin + colW.concepto + colW.fechas, currentY + rowHwithText);
      doc.line(margin + colW.concepto + colW.fechas + colW.incluye, currentY, margin + colW.concepto + colW.fechas + colW.incluye, currentY + rowHwithText);
      doc.line(margin + colW.concepto + colW.fechas + colW.incluye + colW.coste, currentY, margin + colW.concepto + colW.fechas + colW.incluye + colW.coste, currentY + rowHwithText);
    }

    const rowFontSize = compactMode ? 7 : 9;
    const rowTextY = compactMode ? currentY + 3.5 : currentY + 5.5;
    doc.setFontSize(rowFontSize);
    if (mode === "oc-medios") {
      const wrappedConcepto = doc.splitTextToSize(String(r.concepto || ""), wrappedWidthConcepto);
      doc.text(wrappedConcepto, cx + 1.5, rowTextY);
      cx += colW.concepto;
      doc.text(formatEUR(r.inversion), cx + colW.inv - 1.5, rowTextY, { align: "right" });
      cx += colW.inv;
      doc.text(formatEUR(r.fee), cx + colW.fee - 1.5, rowTextY, { align: "right" });
      cx += colW.fee;
      doc.setFont("Montserrat", "bold");
      doc.text(formatEUR(r.total), cx + colW.total - 1.5, rowTextY, { align: "right" });
    } else if (mode === "oc-creatividades") {
      const wrappedConcepto = doc.splitTextToSize(String(r.concepto || ""), wrappedWidthConcepto);
      doc.text(wrappedConcepto, cx + 1.5, rowTextY);
      cx += colW.concepto;
      doc.text(String(r.cantidad), cx + colW.cantidad - 1.5, rowTextY, { align: "right" });
      cx += colW.cantidad;
      doc.text(formatEUR(r.precio), cx + colW.precio - 1.5, rowTextY, { align: "right" });
      cx += colW.precio;
      doc.setFont("Montserrat", "bold");
      doc.text(formatEUR(r.total), cx + colW.total - 1.5, rowTextY, { align: "right" });
    } else if (mode === "oc-proveedores") {
      const wrappedConcepto = doc.splitTextToSize(String(r.concepto || ""), wrappedWidthConcepto);
      doc.text(wrappedConcepto, cx + 1.5, rowTextY);
      cx += colW.concepto;
      doc.text(formatEUR(r.inv), cx + colW.inv - 1.5, rowTextY, { align: "right" });
      cx += colW.inv;
      doc.text(String(r.kpi), cx + colW.kpi - 1.5, rowTextY, { align: "right" });
      cx += colW.kpi;
      doc.text(formatEUR(r.costKpi), cx + colW.costKpi - 1.5, rowTextY, { align: "right" });
      cx += colW.costKpi;
      doc.text(String(r.result ?? ""), cx + colW.result - 1.5, rowTextY, { align: "right" });
      cx += colW.result;
    } else if (mode === "oc-proveedores-incluye") {
      const wrappedConcepto = doc.splitTextToSize(String(r.concepto || ""), wrappedWidthConcepto);
      doc.text(wrappedConcepto, cx + 1.5, rowTextY);
      cx += colW.concepto;
      // CHANGE 2 + 4: usa el flag y formatea fechas sin desfase TZ.
      if (!r.useDateRange) {
        doc.text(String(r.fechaText), cx + 1.5, rowTextY);
      } else {
        doc.text(
          `Inicio: ${formatDateES(r.fechaInicio)}\nFin: ${formatDateES(r.fechaFin)}`,
          cx + 1.5,
          rowTextY
        );
      }
      cx += colW.fechas;
      const wrappedIncluye = doc.splitTextToSize(String(r.incluye || ""), wrappedWidthIncluye);
      doc.text(wrappedIncluye, cx + 1.5, rowTextY);
      cx += colW.incluye;
      doc.setFont("Montserrat", "bold");
      doc.text(formatEUR(r.coste), cx + colW.coste - 1.5, rowTextY, { align: "right" });
    }

    doc.setFont("Montserrat", "normal");

    currentY += rowHwithText;
  });

  // El pie (totales, datos fiscales, metodo de pago y firmas) ocupa
  // `reservedBottom` mm: si no cabe debajo de la tabla, se pasa a una pagina nueva.
  if (currentY > pageH - reservedBottom) {
    doc.addPage([pageW, pageH], "portrait");
    currentY = margin;
  }

  // Totales
  let totalsTop = currentY + 6;
  const tX = margin;
  let tY = totalsTop + 4;
  const lineGap = 6;

  if (mode === "oc-medios") {
    const t = totals as OcTotalsMedios;
    doc.setFont("Montserrat", "bold");
    doc.setFontSize(10);
    doc.text("TOTALES", margin, totalsTop);

    doc.setFont("Montserrat", "normal");
    doc.text("Inversión en medios:", tX, tY);
    doc.text(formatEUR(t.inv), pageW - margin - 1, tY, { align: "right" });

    doc.text("Fee:", tX, tY + lineGap);
    doc.text(formatEUR(t.fee), pageW - margin - 1, tY + lineGap, { align: "right" });

    doc.setFontSize(10);
    doc.setFont("Montserrat", "bold");
    doc.text("TOTAL: (SIN IVA)", tX, tY + lineGap * 2);
    doc.text(formatEUR(t.tot), pageW - margin - 1, tY + lineGap * 2, { align: "right" });
  } else if (mode === "oc-creatividades") {
    const t = totals as OcTotalsCreatividades;
    tY -= lineGap * 2;
    totalsTop -= lineGap * 2;

    doc.setFontSize(10);
    doc.setFont("Montserrat", "bold");
    doc.text("TOTAL: (SIN IVA)", tX, tY + lineGap * 2);
    doc.text(formatEUR(t.tot), pageW - margin - 1, tY + lineGap * 2, { align: "right" });
  } else if (mode === "oc-proveedores") {
    tY -= lineGap * 2;
    totalsTop -= lineGap * 2;

    doc.setFontSize(10);
    doc.setFont("Montserrat", "bold");
    doc.text("TOTAL: (SIN IVA)", tX, tY + lineGap * 2);
    doc.text(formatEUR(totals as number), pageW - margin - 1, tY + lineGap * 2, { align: "right" });
  } else if (mode === "oc-proveedores-incluye") {
    tY -= lineGap * 2;
    totalsTop -= lineGap * 2;

    doc.setFontSize(10);
    doc.setFont("Montserrat", "bold");
    doc.text("TOTAL: (SIN IVA)", tX, tY + lineGap * 2);
    doc.text(formatEUR(totals as number), pageW - margin - 1, tY + lineGap * 2, { align: "right" });
  }
  doc.setFont("Montserrat", "normal");

  // Datos fiscales
  const partiesTop = totalsTop + 26;
  const colGap = 8;
  const colWPart = (contentW - colGap) / 2;
  doc.line(margin, partiesTop - 8, margin + contentW, partiesTop - 8);

  let metodoPagoTop = partiesTop;
  const drawParty = (
    title: string,
    p: any,
    leftX: number,
    isCompany: boolean
  ) => {
    let showInfo = true;
    if (isCompany && !showCompanyContact) showInfo = false;
    if (!isCompany && !showClientContact) showInfo = false;

    doc.setFont("Montserrat", "bold");
    doc.setFontSize(10);
    doc.text(title, leftX, partiesTop);

    doc.setFont("Montserrat", "normal");
    doc.setFontSize(9);

    const baseLines = [
      p.legalName,
      `CIF/NIF: ${p.taxId || ""}`,
      p.address,
      `${p.zip || ""} ${p.city || ""}`,
      p.country,
    ];
    const contactLines = showInfo ? [p.phone, p.email, p.website] : [];
    const lines = [...baseLines, ...contactLines].filter(Boolean);

    let ly = partiesTop + 4;
    lines.forEach((line: string) => {
      doc.text(line, leftX, ly);
      ly += 3.5;
    });

    if (metodoPagoTop < ly + 6) metodoPagoTop = ly + 6;
  };

  if (mode === "oc-proveedores" || mode === "oc-proveedores-incluye") {
    drawParty("DATOS FISCALES DEL PROVEEDOR", client, margin, false);
    drawParty("DATOS FISCALES DEL CLIENTE", company, margin + colWPart + colGap, true);
  } else {
    // oc-medios y oc-creatividades
    drawParty("DATOS FISCALES DEL CLIENTE", client, margin, false);
    drawParty("DATOS FISCALES", company, margin + colWPart + colGap, true);
  }

  // Método de pago
  // CHANGE 3: RBA desde el flag calculado por el backend.
  const isRBA = client?.isRBA === true;

  // Normaliza "paymentTerms" por si viene como "30 días"/"45"/"60"...
  const getPaymentDays = (terms?: string) => {
    const s = String(terms ?? "").trim();
    if (!s) return "60";
    const m = s.match(/(30|45|60)/);
    return m ? m[1] : "60";
  };

  const paymentDays = isRBA ? "60" : getPaymentDays(client.paymentTerms);

  doc.setFont("Montserrat", "bold");
  doc.text("Método de pago:", margin, metodoPagoTop);
  doc.setFont("Montserrat", "normal");

  // 1) Texto principal (si RBA fuerza 60 días)
  const paymentText = `El Anunciante se compromete a abonar las facturas en un plazo de ${paymentDays} días desde la fecha de la emisión de factura, vía; transferencia bancaria.`;

  const wrappedPayment = doc.splitTextToSize(paymentText, contentW);
  const paymentStartY = metodoPagoTop + 5;
  doc.text(wrappedPayment, margin, paymentStartY);

  const lineH = 4;
  let nextY = paymentStartY + wrappedPayment.length * lineH + 2;

  // 2) Texto extra SOLO para RBA
  if (isRBA) {
    const rbaExtra =
      "La inversión es estimada y puede variar, y que, por lo tanto, el fee también se verá afectado en función de la inversión consumida.";
    const wrappedExtra = doc.splitTextToSize(rbaExtra, contentW);
    doc.text(wrappedExtra, margin, nextY);
    nextY += wrappedExtra.length * lineH + 2;
  }

  // 3) IVA debajo de todo
  doc.text("IVA 21% no incluido en orden de compra.", margin, nextY);
  const signBoxH = 28;
  let signTop = nextY + 6;

  if (signTop + (2 + signBoxH) > pageH - margin) {
    doc.addPage([pageW, pageH], "portrait");
    signTop = margin;
  }

  // Firma
  doc.setFont("Montserrat", "normal");
  doc.setFontSize(9);
  if (mode === "oc-proveedores" || mode === "oc-proveedores-incluye") {
    doc.text("Firma Proveedor", margin, signTop);
    doc.text("Firma Cliente", margin + colWPart + colGap, signTop);
  } else {
    doc.text("Firma Cliente", margin, signTop);
    doc.text("Firma Proveedor", margin + colWPart + colGap, signTop);
  }
  doc.rect(margin, signTop + 2, colWPart, 28);
  doc.rect(margin + colWPart + colGap, signTop + 2, colWPart, 28);

  // Guardar
  // CHANGE: filename = {issuerCode}-{slug(clientLabel)}-{ocDate}.pdf
  const issuerCode = slug(company.code || "OC");
  const clientName = slug(client.label || client.legalName || "cliente");
  const ocDateSafe = slug(ocDate || "fecha");
  const filename = `${issuerCode}-${clientName}-${ocDateSafe}.pdf`;
  doc.save(filename);
}
